import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { IntegrationConfig } from "../types";
import { Card } from "@/components/ui/card";

interface IntegrationSettingsProps {
  settings: IntegrationConfig;
  onChange: (settings: IntegrationConfig) => void;
  isPro: boolean;
}

export const IntegrationSettings = ({ settings, onChange, isPro }: IntegrationSettingsProps) => {
 
  const validateCode = (code: string): boolean => {
    // Basic validation for properly closed script tags
    const openTags = (code.match(/<script/g) || []).length;
    const closeTags = (code.match(/<\/script>/g) || []).length;
    return openTags === closeTags;
  };

  const handleCodeChange = (field: keyof IntegrationConfig, value: string) => {
    if (!validateCode(value)) {
      // Show warning but still allow saving
      console.warn('Warning: Script tags might not be properly closed');
    }
    
    onChange({
      ...settings,
      [field]: value
    });
  };

  return (
    <div className="space-y-6">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Use this section to add custom tracking codes, analytics, or integrations (e.g., Facebook Pixel, Google Tag Manager, custom JavaScript).
          Make sure to test your code thoroughly as invalid scripts may affect your funnel's functionality.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="headCode">Head Code</Label>
          <Textarea
            id="headCode"
            value={settings.headCode}
            onChange={(e) => handleCodeChange('headCode', e.target.value)}
            placeholder="<script>/* Your head code here */</script>"
            className="font-mono min-h-[150px] bg-slate-50"
          />
          <p className="text-sm text-muted-foreground">
            Code to be placed in the &lt;head&gt; section of the funnel view.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="bodyCode">Body Code</Label>
          <Textarea
            id="bodyCode"
            value={settings.bodyCode}
            onChange={(e) => handleCodeChange('bodyCode', e.target.value)}
            placeholder="<script>/* Your body code here */</script>"
            className="font-mono min-h-[150px] bg-slate-50"
          />
          <p className="text-sm text-muted-foreground">
            Code to be placed before the closing &lt;body&gt; tag.
          </p>
        </div>
      </div>
    </div>
  );
};